// European date format utilities (CET +1)
// Format: DD/MM/YYYY HH:mm (24-hour)

/**
 * Parse API timestamps as UTC (backend stores UTC, often without offset suffix).
 */
export const parseUtcDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  const str = String(dateStr).trim();
  if (!str) return null;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(str)) {
    return new Date(`${str}Z`);
  }
  return new Date(str);
};

/**
 * Format date to European format DD/MM/YYYY HH:mm (CET +1)
 * @param {string|Date} dateStr - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = parseUtcDate(dateStr);
    if (isNaN(date.getTime())) return '-';
    
    // Format for CET timezone (Europe/Tirane for Albania)
    return date.toLocaleString('sq-AL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/Tirane'
    });
  } catch (e) {
    return '-';
  }
};

/**
 * Format date only to European format DD/MM/YYYY
 * @param {string|Date} dateStr - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = parseUtcDate(dateStr);
    if (isNaN(date.getTime())) return '-';
    
    return date.toLocaleDateString('sq-AL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/Tirane'
    });
  } catch (e) {
    return '-';
  }
};

/**
 * Format time only to 24-hour format HH:mm (CET +1)
 * @param {string|Date} dateStr - Date string or Date object
 * @returns {string} Formatted time string
 */
export const formatTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = parseUtcDate(dateStr);
    if (isNaN(date.getTime())) return '-';
    
    return date.toLocaleTimeString('sq-AL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/Tirane'
    });
  } catch (e) {
    return '-';
  }
};

/**
 * Format relative time (e.g., "5 minuta më parë")
 * @param {string|Date} dateStr - Date string or Date object
 * @returns {string} Relative time string in Albanian
 */
export const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = parseUtcDate(dateStr);
    if (isNaN(date.getTime())) return '-';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Tani';
    if (diffMins < 60) return `${diffMins} min më parë`;
    if (diffHours < 24) return `${diffHours} orë më parë`;
    if (diffDays < 7) return `${diffDays} ditë më parë`;
    
    return formatDate(dateStr);
  } catch (e) {
    return '-';
  }
};

export default {
  parseUtcDate,
  formatDateTime,
  formatDate,
  formatTime,
  formatRelativeTime
};
